import { FC, useEffect, useRef, useState } from 'react';
import { usePostHog } from '@posthog/react';
import { FlaskConical, ChevronUp, MessageSquare, RotateCcw, Undo2 } from 'lucide-react';
import { setBetaNavExited } from './betaNavPreference';

interface Props {
  onReplay: () => void;
}

export const BetaChip: FC<Props> = ({ onReplay }) => {
  const [open, setOpen] = useState(false);
  const posthog = usePostHog();
  const chip = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsidePress = (event: MouseEvent) => {
      if (!chip.current?.contains(event.target as Node)) setOpen(false);
    };

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsidePress);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePress);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const replay = () => {
    setOpen(false);
    onReplay();
  };

  const sendFeedback = () => setOpen(false);

  const exitBeta = () => {
    posthog?.capture('beta_nav_exited');
    posthog?.setPersonProperties({ side_nav_beta_status: 'exited' });
    setBetaNavExited(true);
    window.location.assign('/');
  };

  return (
    <div className="nc_beta-chip" ref={chip}>
      {open && (
        <div className="nc_beta-chip__menu" role="menu">
          <button type="button" role="menuitem" className="nc_beta-chip__item" onClick={replay}>
            <RotateCcw size={18} aria-hidden="true" />
            Replay the tour
          </button>
          <button
            type="button"
            role="menuitem"
            id="betaChipFeedbackLink"
            className="nc_beta-chip__item"
            onClick={sendFeedback}
          >
            <MessageSquare size={18} aria-hidden="true" />
            Send feedback
          </button>
          <button
            type="button"
            role="menuitem"
            id="sideNavBetaExitButton"
            className="nc_beta-chip__item nc_beta-chip__item--exit"
            onClick={exitBeta}
          >
            <Undo2 size={18} aria-hidden="true" />
            Switch back to the old navigation
          </button>
        </div>
      )}

      <button
        type="button"
        className="nc_beta-chip__toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
      >
        <FlaskConical size={16} aria-hidden="true" />
        Beta
        <ChevronUp size={16} aria-hidden="true" className={open ? 'nc_beta-chip__caret--open' : undefined} />
      </button>
    </div>
  );
};

export default BetaChip;
